import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Sparkles, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

export default function Assistant() {
  const { t } = useI18n();
  const [q, setQ] = useState('');
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (q.trim().length < 3 || loading) return;
    const question = q.trim();
    setQ('');
    setConversations((c) => [...c, { role: 'user', text: question }]);
    setLoading(true);
    try {
      const { data } = await api.post('/ai/ask', { question });
      setConversations((c) => [...c, { role: 'assistant', text: data.answer, sources: data.sources || [] }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.common.error);
      setConversations((c) => [...c, { role: 'assistant', text: t.common.error, sources: [] }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="flex items-center gap-3 mb-4">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-gold/20 text-brand-gold-dark">
          <Sparkles size={18} />
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Tikkun AI</p>
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-tight">{t.assistant.title}</h1>
      <p className="mt-3 text-lg text-muted-foreground">{t.assistant.subtitle}</p>

      <div className="mt-10 space-y-4" data-testid="assistant-conversation">
        {conversations.map((m, i) => (
          <div key={i} data-testid={`msg-${i}`} className={`p-5 rounded-3xl ${m.role === 'user' ? 'bg-primary text-primary-foreground ml-8' : 'bg-secondary mr-8'}`}>
            <div className="text-xs uppercase tracking-widest opacity-70 mb-2">{m.role === 'user' ? 'You' : 'Tikkun AI'}</div>
            <p className="leading-relaxed whitespace-pre-line">{m.text}</p>
            {m.role === 'assistant' && m.sources?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border/50">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t.assistant.sources}</div>
                <div className="space-y-1.5">
                  {m.sources.map((s) => (
                    <Link key={s.id} to={`/library/${s.id}`} data-testid={`source-link-${s.id}`}
                      className="flex items-center gap-2 text-sm hover:text-brand-gold-dark transition-colors">
                      <ArrowRight size={12} /> <span className="underline underline-offset-2">{s.title}</span>
                      <span className="text-xs text-muted-foreground">· {s.reliability}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="p-5 rounded-3xl bg-secondary mr-8">
            <div className="text-xs uppercase tracking-widest opacity-70 mb-2">Tikkun AI</div>
            <p className="text-muted-foreground">{t.assistant.thinking}</p>
          </div>
        )}
      </div>

      <form onSubmit={submit} className="mt-10 flex gap-3 sticky bottom-6">
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.assistant.ph}
          data-testid="assistant-input" disabled={loading}
          className="flex-1 px-5 py-4 rounded-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none text-sm shadow-xl shadow-black/5" />
        <button type="submit" disabled={loading || q.trim().length < 3} data-testid="assistant-submit-btn"
          className="inline-flex items-center gap-2 px-6 py-4 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-40 transition-opacity shadow-xl shadow-black/5">
          <Send size={16} /> {t.assistant.ask}
        </button>
      </form>
      <p className="mt-4 text-xs text-muted-foreground text-center">{t.assistant.disclaimer}</p>
    </div>
  );
}
